import { BASE_URL, getPageUrl, type SupportedLocale } from './seo';

export { BASE_URL };

// ─── Organization ───────────────────────────────────────────────────────────

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Peternity',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/og-image.png`,
      width: 1200,
      height: 630,
    },
    sameAs: [
      'https://instagram.com/peternity.io',
      'https://twitter.com/peternityhq',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@peternity.io',
    },
  };
}

// ─── WebSite (Sitelinks Searchbox eligible) ─────────────────────────────────

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: 'Peternity',
    inLanguage: ['en', 'ru', 'es'],
    publisher: { '@id': `${BASE_URL}/#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/blog?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

// ─── SoftwareApplication ─────────────────────────────────────────────────────

const APP_DESCRIPTIONS: Record<SupportedLocale, string> = {
  en: 'Peternity — AI pet companion that keeps your pet\'s personality alive forever. Chat, memories, digital twin, and kawaii portraits.',
  ru: 'Peternity — ИИ-компаньон питомца, который сохраняет личность вашего питомца навсегда. Чат, воспоминания, цифровой двойник и кавайные портреты.',
  es: 'Peternity — compañero de mascota con IA que mantiene viva la personalidad de tu mascota para siempre. Chat, recuerdos, gemelo digital y retratos kawaii.',
};

const APP_NAMES: Record<SupportedLocale, string> = {
  en: 'Peternity — AI Pet Companion',
  ru: 'Peternity — ИИ Компаньон Питомца',
  es: 'Peternity — Compañero IA de Mascotas',
};

export function buildSoftwareApplicationSchema(locale: SupportedLocale = 'en') {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${BASE_URL}/#app`,
    name: APP_NAMES[locale],
    url: getPageUrl('home', locale),
    description: APP_DESCRIPTIONS[locale],
    applicationCategory: 'LifestyleApplication',
    applicationSubCategory: 'Pet Care',
    operatingSystem: 'Web, iOS, Android',
    isAccessibleForFree: true,
    inLanguage: ['en', 'ru', 'es'],
    featureList: [
      'AI pet companion with personality modeling — 8 archetypes, 6 intelligence levels',
      'Persistent memory — your pet remembers every conversation',
      'Rainbow Bridge digital memorial with photo gallery and community candles',
      'Kids 30-day responsibility challenge for ages 6–12 with breed recommendation',
      'AI-generated kawaii pet portraits',
      'Auto-generated pet diary entries',
      'Community Memory Garden with candles and condolences',
      'COPPA and GDPR compliant, no ads, no data selling',
      'Fully localized in English, Russian, and Spanish',
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '247',
      bestRating: '5',
      worstRating: '1',
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'Free',
        price: '0',
        priceCurrency: 'USD',
        description: '1 pet, 10 messages/day, 1 memorial (5 photos), 10 journal entries, basic personality. No credit card required.',
      },
      {
        '@type': 'Offer',
        name: 'Plus',
        price: '9.99',
        priceCurrency: 'USD',
        description: '3 pets, unlimited messages, 3 memorials, AI portraits (5/mo)',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '9.99',
          priceCurrency: 'USD',
          unitCode: 'MON',
        },
      },
      {
        '@type': 'Offer',
        name: 'Forever',
        price: '19.99',
        priceCurrency: 'USD',
        description: 'Unlimited pets, memorials, messages, photos, and Rainbow Bridge mode',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '19.99',
          priceCurrency: 'USD',
          unitCode: 'MON',
        },
      },
    ],
    publisher: { '@id': `${BASE_URL}/#organization` },
  };
}

// ─── HowTo ─────────────────────────────────────────────────────────────────

const HOW_TO_NAMES: Record<SupportedLocale, string> = {
  en: 'How to create an AI pet companion with Peternity',
  ru: 'Как создать ИИ-компаньона питомца в Peternity',
  es: 'Cómo crear un compañero IA para tu mascota con Peternity',
};

const HOW_TO_DESCRIPTIONS: Record<SupportedLocale, string> = {
  en: 'Set up your personalized AI pet companion in under 2 minutes.',
  ru: 'Создайте персонализированного ИИ-компаньона питомца менее чем за 2 минуты.',
  es: 'Configura tu compañero IA personalizado para tu mascota en menos de 2 minutos.',
};

const HOW_TO_STEPS: Record<SupportedLocale, Array<{ name: string; text: string }>> = {
  en: [
    { name: 'Tell Us About Your Pet', text: "Share your pet's name, breed, personality traits, and favorite memories. The more you share, the more accurate the AI becomes." },
    { name: 'AI Creates the Personality', text: "Peternity's AI builds a unique personality matrix — selecting from 8 archetypes, setting intelligence level, and learning your pet's quirks." },
    { name: 'Start Talking', text: 'Chat naturally, play, feed, walk — and watch your pet remember everything. Conversations build on top of each other over time.' },
  ],
  ru: [
    { name: 'Расскажите о питомце', text: 'Поделитесь именем, породой, чертами характера и любимыми воспоминаниями. Чем больше вы расскажете, тем точнее станет ИИ.' },
    { name: 'ИИ создаёт личность', text: 'ИИ Peternity строит уникальную матрицу личности — выбирает архетип, задаёт уровень интеллекта и учит особенности вашего питомца.' },
    { name: 'Начните общение', text: 'Общайтесь, играйте, кормите, гуляйте — и наблюдайте, как питомец запоминает всё. Разговоры накапливаются со временем.' },
  ],
  es: [
    { name: 'Cuéntanos sobre tu mascota', text: 'Comparte el nombre, raza, rasgos de personalidad y recuerdos favoritos. Cuanto más compartas, más precisa será la IA.' },
    { name: 'La IA crea la personalidad', text: 'La IA de Peternity construye una matriz de personalidad única — selecciona un arquetipo, establece el nivel de inteligencia y aprende los rasgos de tu mascota.' },
    { name: 'Empieza a hablar', text: 'Chatea, juega, alimenta, pasea — y observa cómo tu mascota recuerda todo. Las conversaciones se acumulan con el tiempo.' },
  ],
};

export function buildHowToSchema(locale: SupportedLocale = 'en') {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: HOW_TO_NAMES[locale],
    description: HOW_TO_DESCRIPTIONS[locale],
    totalTime: 'PT2M',
    step: HOW_TO_STEPS[locale].map((s, i) => ({
      '@type': 'HowToStep',
      position: String(i + 1),
      name: s.name,
      text: s.text,
    })),
  };
}

// ─── ItemList (features) ──────────────────────────────────────────────────────

export function buildFeatureItemListSchema(locale: SupportedLocale = 'en') {
  const features = [
    { name: 'AI Pet Companion', url: `${BASE_URL}${locale === 'ru' ? '/ru' : locale === 'es' ? '/es' : ''}/ai-pet-companion` },
    { name: 'Rainbow Bridge Memorial', url: `${BASE_URL}${locale === 'ru' ? '/ru' : locale === 'es' ? '/es' : ''}/pet-memorial` },
    { name: 'Kids Responsibility Challenge', url: `${BASE_URL}${locale === 'ru' ? '/ru' : locale === 'es' ? '/es' : ''}/pricing` },
    { name: 'AI Pet Portraits', url: `${BASE_URL}${locale === 'ru' ? '/ru' : locale === 'es' ? '/es' : ''}/pricing` },
    { name: 'Memory Garden', url: `${BASE_URL}${locale === 'ru' ? '/ru' : locale === 'es' ? '/es' : ''}/garden` },
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Peternity Features',
    numberOfItems: features.length,
    itemListElement: features.map((f, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: f.name,
      url: f.url,
    })),
  };
}

// ─── FAQPage ─────────────────────────────────────────────────────────────────

export interface FaqItem {
  question: string;
  answer: string;
}

export function buildFAQPageSchema(faqs: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ─── BreadcrumbList ──────────────────────────────────────────────────────────

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ─── Article ─────────────────────────────────────────────────────────────────

export interface ArticleSchemaProps {
  headline: string;
  description: string;
  url: string;
  imageUrl?: string;
  datePublished: string;
  dateModified?: string;
  authorName?: string;
  locale?: SupportedLocale;
}

export function buildArticleSchema(props: ArticleSchemaProps) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: props.headline,
    description: props.description,
    url: props.url,
    ...(props.imageUrl && {
      image: {
        '@type': 'ImageObject',
        url: props.imageUrl,
        width: 1200,
        height: 630,
      },
    }),
    datePublished: props.datePublished,
    dateModified: props.dateModified ?? props.datePublished,
    author: {
      '@type': 'Organization',
      name: props.authorName ?? 'Peternity',
      url: BASE_URL,
    },
    publisher: { '@id': `${BASE_URL}/#organization` },
    inLanguage: props.locale ?? 'en',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': props.url,
    },
  };
}

// ─── About / Extended Organization ───────────────────────────────────────────

export function buildAboutOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: 'Peternity',
    alternateName: ['Peternity App', 'Peternity AI'],
    slogan: 'Your Pet\'s Digital Soul',
    url: BASE_URL,
    description:
      'AI-powered pet companion and memorial platform. We help pet owners celebrate their companions, preserve precious memories, and find comfort in moments of loss.',
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/og-image.png`,
      width: 1200,
      height: 630,
    },
    foundingDate: '2025',
    founder: {
      '@type': 'Person',
      name: 'Pavel Rabtsevich',
      jobTitle: 'Founder & CEO',
      url: 'https://www.linkedin.com/in/pavel-rabtsevich/',
      sameAs: 'https://www.linkedin.com/in/pavel-rabtsevich/',
    },
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'US',
      description: 'Remote team',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@peternity.app',
        availableLanguage: ['English', 'Russian', 'Spanish'],
      },
      {
        '@type': 'ContactPoint',
        contactType: 'press',
        email: 'press@peternity.app',
      },
    ],
    sameAs: [
      'https://instagram.com/peternity.app',
      'https://twitter.com/peternityhq',
    ],
    knowsAbout: [
      'AI pet companion',
      'digital pet memorial',
      'pet grief support',
      'pet personality AI',
      'Rainbow Bridge memorial',
      'kids pet responsibility challenge',
    ],
  };
}

// ─── Founder Person ───────────────────────────────────────────────────────────

export function buildFounderPersonSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': 'https://www.linkedin.com/in/pavel-rabtsevich/#person',
    name: 'Pavel Rabtsevich',
    jobTitle: 'Founder & CEO',
    url: 'https://www.linkedin.com/in/pavel-rabtsevich/',
    sameAs: [
      'https://www.linkedin.com/in/pavel-rabtsevich/',
    ],
    worksFor: {
      '@type': 'Organization',
      '@id': `${BASE_URL}/#organization`,
      name: 'Peternity',
    },
    knowsAbout: [
      'AI pet companions',
      'pet memorials',
      'grief technology',
      'pet care applications',
      'pet loss support',
    ],
  };
}

// ─── WebPage ─────────────────────────────────────────────────────────────────

export interface WebPageSchemaProps {
  name: string;
  description: string;
  url: string;
  imageUrl?: string;
  locale?: SupportedLocale;
  breadcrumb?: BreadcrumbItem[];
}

export function buildWebPageSchema(props: WebPageSchemaProps) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${props.url}#webpage`,
    name: props.name,
    description: props.description,
    url: props.url,
    inLanguage: props.locale ?? 'en',
    isPartOf: { '@id': `${BASE_URL}/#website` },
    ...(props.imageUrl && {
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: props.imageUrl,
      },
    }),
    ...(props.breadcrumb && {
      breadcrumb: buildBreadcrumbSchema(props.breadcrumb),
    }),
    publisher: { '@id': `${BASE_URL}/#organization` },
  };
}
